import { useCallback, useMemo, useState } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { OptionSelect } from '@/core/utils/types'
import { GrupoUsuariosValues } from './use-form'
import { useGrupoUsuariosEdicaoQueryHandler } from './use-query-handler'

interface useGrupoUsuariosEdicaoProps {
  control: Control<GrupoUsuariosValues>
}

export const useGrupoUsuariosEdicao = ({
  control
}: useGrupoUsuariosEdicaoProps) => {
  const values = useWatch<GrupoUsuariosValues>({ control })
  const {
    getGrupoUsuariosDominioQuery: { data }
  } = useGrupoUsuariosEdicaoQueryHandler()
  const { dealers, usuarioLogado } = data ?? {}

  const {
    tiposUsuarios: tiposUsuariosSelecionados,
    possuiPerfil,
    possuiUsuariosManuais,
    possuiCargos,
    possuiSetores,
    possuiGrupos,
    possuiDepartamentos,
    marcas: marcasSelecionadas
  } = values

  const [isVisualizarDestinatariosOpen, setIsVisualizarDestinatariosOpen] =
    useState(false)

  const getOptions = useMemo(() => {
    if (!usuarioLogado) return []
    const marcas = usuarioLogado?.marcas
    const tiposUsuarios = usuarioLogado?.tiposUsuarios
    const produtos = usuarioLogado?.produtos

    const marcasOptions = (marcas ?? []).map(marca => ({
      value: marca?.id,
      label: marca?.description
    }))

    const tiposUsuariosOptions = (tiposUsuarios ?? []).map(tipoUsuario => ({
      value: tipoUsuario.id,
      label: tipoUsuario.description
    }))

    const dealersOptions =
      (dealers ?? [])
        .filter(({ active }) => active)
        .filter(({ marcas }) =>
          (marcas ?? []).some(marca =>
            (marcasSelecionadas ?? []).map(
              (marcaSelecionada: OptionSelect) =>
                marcaSelecionada.value === marca?.id
            )
          )
        )
        .map(({ id, nomeExibicao }) => ({
          value: id,
          label: nomeExibicao
        })) ?? []

    const produtosOptions = (produtos ?? []).reduce(
      (map: Map<string, OptionSelect[]>, produto) => {
        const tiposAcesso: OptionSelect[] = (produto?.tiposAcesso ?? [])
          .map(tipoAcesso => ({
            value: tipoAcesso?.id,
            label: tipoAcesso?.nome
          }))
          .filter(tipo =>
            tiposUsuariosSelecionados
              ?.map(t => t.label?.toLowerCase())
              .some(palavra =>
                tipo?.label?.toLowerCase().includes(palavra?.toLowerCase())
              )
          )
          .sort((a, b) =>
            (a.label ?? '').localeCompare(b.label ?? '', 'pt-BR', {
              sensitivity: 'base'
            })
          )
        map.set(produto.nome, tiposAcesso)
        return map
      },
      new Map()
    )

    return {
      marcasOptions,
      tiposUsuariosOptions,
      dealersOptions,
      produtosOptions
    }
  }, [usuarioLogado, dealers, marcasSelecionadas])

  const handleOpenVisualizarDestinatarios = useCallback(() => {
    setIsVisualizarDestinatariosOpen(true)
  }, [])

  const handleCloseVisualizarDestinatarios = useCallback(() => {
    setIsVisualizarDestinatariosOpen(false)
  }, [])

  return {
    isVisualizarDestinatariosOpen,
    handleOpenVisualizarDestinatarios,
    handleCloseVisualizarDestinatarios,
    possuiUsuariosManuais,
    possuiPerfil,
    getOptions,
    tiposUsuariosSelecionados,
    possuiCargos,
    possuiSetores,
    possuiGrupos,
    possuiDepartamentos
  }
}
